import React, { useRef, useState, useCallback } from "react";

/**
 * Wraps any trigger (button, link, icon) and shows a styled tooltip on
 * hover/focus. Consistent, accessible replacement for the native `title`
 * attribute across the app.
 *
 * Top/bottom tooltips are viewport-aware: if a centered tooltip would run off
 * the left/right edge of the screen (e.g. the last item in a row, or on
 * mobile), it shifts back into view so the text is always fully visible.
 */
export default function Tooltip({
  label,
  children,
  side = "top",
  wide = false,
  className = "",
}) {
  const tipRef = useRef(null);
  const [shift, setShift] = useState(0);
  const isVertical = side === "top" || side === "bottom";

  // Measure the tooltip's natural (centered) position and nudge it horizontally
  // so it stays within the viewport with an 8px margin.
  const reposition = useCallback(() => {
    const el = tipRef.current;
    if (!el || !isVertical) return;
    el.style.transform = "translateX(-50%)";
    const rect = el.getBoundingClientRect();
    const margin = 8;
    let s = 0;
    if (rect.left < margin) s = margin - rect.left;
    else if (rect.right > window.innerWidth - margin) {
      s = window.innerWidth - margin - rect.right;
    }
    setShift(s);
  }, [isVertical]);

  if (!label) return children;

  const posClass = {
    top: "bottom-full mb-2",
    bottom: "top-full mt-2",
    left: "right-full top-1/2 -translate-y-1/2 mr-2",
    right: "left-full top-1/2 -translate-y-1/2 ml-2",
  }[side];

  // If the caller positions the wrapper itself (absolute/fixed/sticky), don't
  // force `relative`.
  const hasPosition = /(^|\s)(absolute|fixed|sticky)(\s|$)/.test(className);

  const verticalStyle = isVertical
    ? { left: "50%", transform: `translateX(calc(-50% + ${shift}px))` }
    : undefined;

  return (
    <span
      className={`${hasPosition ? "" : "relative"} inline-flex group/tt ${className}`}
      onMouseEnter={reposition}
      onFocus={reposition}
    >
      {children}
      <span
        ref={tipRef}
        role="tooltip"
        style={verticalStyle}
        className={[
          "pointer-events-none absolute z-50 rounded-lg border border-white/10",
          "bg-surface-light/98 backdrop-blur-md px-2.5 py-1.5 text-xs font-medium text-white/90",
          "shadow-[0_8px_24px_rgba(0,0,0,0.55)] opacity-0 transition-opacity duration-150",
          "group-hover/tt:opacity-100 group-focus-within/tt:opacity-100",
          wide
            ? "w-52 max-w-[calc(100vw-16px)] whitespace-normal text-center leading-relaxed"
            : "max-w-[calc(100vw-16px)] whitespace-nowrap",
          posClass,
        ].join(" ")}
      >
        {label}
      </span>
    </span>
  );
}
